import { formatEuros } from '../format.ts';

export interface InvoiceParty {
  companyName?: string;
  legalName?: string;
  siret?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  vatRegime?: 'FRANCHISE' | 'LIABLE';
  vatNumber?: string | null;
}

export interface Invoice {
  id: string;
  number: string;
  status: 'REQUESTED' | 'PAID' | 'REJECTED';
  subtotalCents: number;
  vatRatePct: number;
  vatCents: number;
  totalCents: number;
  issuer: InvoiceParty;
  recipient: InvoiceParty;
  lines: { commissionId: string; date: string; description: string; amountCents: number }[];
  issuedAt: string;
  mandateAcceptedAt: string;
  paidAt: string | null;
  paidMethod: 'STRIPE' | 'MANUAL' | null;
  paidReference: string | null;
  rejectedReason: string | null;
  referrerName?: string;
  stripeConnected?: boolean;
}

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR');

function Party({ title, party }: { title: string; party: InvoiceParty }) {
  return (
    <div style={{ flex: 1, minWidth: 220 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 6 }}>{title}</div>
      <div style={{ fontWeight: 800 }}>{party.companyName ?? party.legalName}</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        {party.address}<br />
        {party.postalCode} {party.city}<br />
        SIRET {party.siret}
        {party.vatNumber ? <><br />TVA {party.vatNumber}</> : null}
      </div>
    </div>
  );
}

export function InvoiceView({ invoice }: { invoice: Invoice }) {
  return (
    <div>
      <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" onClick={() => window.print()}>Imprimer / enregistrer en PDF</button>
        {invoice.status === 'PAID' && (
          <span className="badge badge-available">
            Payée le {invoice.paidAt ? fmtDate(invoice.paidAt) : ''} · {invoice.paidMethod === 'STRIPE' ? 'virement Stripe' : 'virement'}
            {invoice.paidReference ? ` (${invoice.paidReference})` : ''}
          </span>
        )}
        {invoice.status === 'REQUESTED' && <span className="badge badge-pending">En attente de paiement</span>}
        {invoice.status === 'REJECTED' && <span className="badge badge-unavailable">Annulée : {invoice.rejectedReason}</span>}
      </div>

      <div className="card invoice-sheet" style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>FACTURE</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Autofacturation</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 13 }}>
            <div style={{ fontWeight: 800 }}>N° {invoice.number}</div>
            <div>Date d'émission : {fmtDate(invoice.issuedAt)}</div>
            <div>Échéance : à réception</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Party title="Émetteur" party={invoice.issuer} />
          <Party title="Destinataire" party={invoice.recipient} />
        </div>

        <div className="table-wrap" style={{ border: 'none' }}>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Désignation</th><th style={{ textAlign: 'right' }}>Montant HT</th></tr></thead>
            <tbody>
              {invoice.lines.map((l) => (
                <tr key={l.commissionId}>
                  <td>{fmtDate(l.date)}</td>
                  <td>{l.description}</td>
                  <td style={{ textAlign: 'right' }}>{formatEuros(l.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ alignSelf: 'flex-end', minWidth: 260, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total HT</span><span>{formatEuros(invoice.subtotalCents)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>TVA {invoice.vatRatePct > 0 ? `${invoice.vatRatePct} %` : ''}</span><span>{formatEuros(invoice.vatCents)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 16, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
            <span>Total TTC</span><span>{formatEuros(invoice.totalCents)}</span>
          </div>
        </div>

        <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.6, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          {invoice.issuer.vatRegime === 'FRANCHISE' && <div>TVA non applicable, art. 293 B du CGI.</div>}
          <div>
            Facture établie par {invoice.recipient.legalName} au nom et pour le compte de {invoice.issuer.companyName}, en vertu du mandat
            de facturation accepté le {fmtDate(invoice.mandateAcceptedAt)}.
          </div>
          <div>
            En cas de retard de paiement : pénalités égales à trois fois le taux d'intérêt légal et indemnité forfaitaire de
            recouvrement de 40 €.
          </div>
        </div>
      </div>
    </div>
  );
}
